import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Sustainability() {
  const sustainabilityInitiatives = [
    {
      title: 'Training the Trainers',
      description: 'Olatunde International Limited remains committed to training trainers at the start of each production year, both in closed sessions and on-field, to ensure effective knowledge transfer and improved farmer productivity. This comprehensive program equips agricultural extension officers and lead farmers with the latest techniques in sustainable cocoa farming, quality management, and best practices.',
      icon: 'ri-team-fill',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/ad8cc4f4c316b8782cff027cc697d88f.jpeg',
      stats: ['Annual Training', 'Field & Classroom', '100+ Trainers']
    },
    {
      title: 'Climate-Friendly Cocoa Farming',
      description: 'We support and promote regenerative agriculture by helping farmers adopt Good Agricultural Practices (GAP), agroforestry, and soil conservation techniques. These sustainable methods improve productivity, enhance soil health, and protect biodiversity across cocoa-growing regions.',
      icon: 'ri-leaf-fill',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/91e7761f91b9e720b8ad53f10a8d49f2.png',
      stats: ['50+ Farmers Trained', 'GAP Certified', '25% Yield Increase']
    },
    {
      title: 'Child Labour Monitoring & Education',
      description: 'Through our Child Labour Monitoring and Remediation Systems (CLMRS) and "Back to School" programmes, we work to ensure that children in cocoa-growing communities are safe, educated, and empowered. Our initiatives help prevent child labour, support school enrollment, and create better futures for the next generation.',
      icon: 'ri-graduation-cap-fill',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/b6dc6573b23aa3df4df687cbfdacc391.jpeg',
      stats: ['200+ Children in School', 'Zero Child Labour', '98% Enrollment Rate']
    },
    {
      title: 'Women Empowerment',
      description: 'We actively invest in women-led farming initiatives, providing targeted training, promoting financial inclusion, and creating leadership opportunities that foster equity, economic independence, and stronger cocoa-growing communities.',
      icon: 'ri-user-heart-fill',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/2a7a7708d5350e4f94936bff4e8eed0c.png',
      stats: ['150+ Women Trained', '40% Female Leadership', '$50K+ Microloans']
    },
    {
      title: 'Deforestation-Free Supply Chains',
      description: 'All our cocoa is fully traceable from farm to port, meeting the strict requirements of the EU Deforestation Regulation (EUDR) and global sustainability standards, including the Rainforest Alliance. This traceability ensures transparency, ethical sourcing, and environmental responsibility throughout our supply chain.',
      icon: 'ri-tree-fill',
      image: 'https://readdy.ai/api/search-image?query=Protected%20forest%20areas%20with%20cocoa%20farming%2C%20dense%20green%20tropical%20forest%20canopy%2C%20sustainable%20cocoa%20plantations%20integrated%20with%20natural%20forest%2C%20GPS%20tracking%20and%20monitoring%20technology%2C%20satellite%20imagery%20of%20preserved%20forest%20areas%2C%20environmental%20conservation%2C%20lush%20green%20ecosystem&width=600&height=400&seq=sustainability4&orientation=landscape',
      stats: ['100% Traceable', 'EUDR Compliant', '0% Deforestation']
    },
    {
      title: 'Community-Led Development',
      description: 'We engage directly with farmers and local stakeholders to build long-term, inclusive programs that prioritize livelihoods, enhance food security, and strengthen rural resilience across cocoa-growing communities.',
      icon: 'ri-community-fill',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/18146036a34ddbb4232a7e1d13d768a0.jpeg',
      stats: ['20+ Communities', '95% Food Security', '30+ Local Projects']
    }
  ];

  const certifications = [
    { name: 'Rainforest Alliance', icon: 'ri-shield-check-fill' },
    { name: 'EUDR Compliant', icon: 'ri-government-fill' },
    { name: 'Fairtrade Certified', icon: 'ri-hand-heart-fill' },
    { name: 'Organic Certified', icon: 'ri-plant-fill' }
  ];

  const impactStats = [
    { value: '500+', label: 'Farmers Supported', icon: 'ri-user-3-fill' },
    { value: '2,000+', label: 'Hectares Protected', icon: 'ri-landscape-fill' },
    { value: '50+', label: 'Communities Reached', icon: 'ri-building-2-fill' },
    { value: '100%', label: 'Traceable Supply Chain', icon: 'ri-links-fill' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/9916585bab92e6ef2dba2688bb5fa25b.png')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Building a Sustainable Future
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              At Olatunde International Limited, sustainability isn't just a commitment, it's the foundation of everything we do. 
              As a leading exporter of Nigerian cocoa, we recognize our responsibility to protect the environment, uplift rural communities, 
              and promote ethical trade practices across every link of our supply chain.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact" 
                className="bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Partner With Us
              </Link>
              <Link 
                to="/market-segment" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-green-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Our Impact
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Sustainability Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Measurable results from our commitment to environmental stewardship and community empowerment
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-2xl text-amber-600`}></i>
                </div>
                <div className="text-4xl font-bold text-green-800 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Initiatives */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Sustainability Initiatives</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive programs addressing environmental protection, social responsibility, and economic empowerment
            </p>
          </div>

          <div className="space-y-20">
            {sustainabilityInitiatives.map((initiative, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}>
                <div className="lg:w-1/2">
                  <img 
                    src={initiative.image}
                    alt={initiative.title}
                    className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                      <i className={`${initiative.icon} text-2xl text-amber-600`}></i>
                    </div>
                    <h3 className="text-3xl font-bold text-green-800">{initiative.title}</h3>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed mb-8">
                    {initiative.description}
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    {initiative.stats.map((stat, statIndex) => (
                      <div key={statIndex} className="text-center bg-green-50 rounded-lg p-4">
                        <div className="font-bold text-green-800">{stat}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Standards */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Certifications & Standards</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meeting and exceeding global sustainability and quality standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${cert.icon} text-2xl text-green-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-green-800">{cert.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Goals */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-green-800 to-green-700 rounded-3xl p-12 text-white text-center">
            <h2 className="text-4xl font-bold mb-6">Our 2030 Sustainability Goals</h2>
            <p className="text-xl text-green-100 mb-12 max-w-4xl mx-auto">
              Ambitious targets that drive our commitment to environmental stewardship, social responsibility, and economic empowerment
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 rounded-xl p-6">
                <div className="text-3xl font-bold text-amber-400 mb-2">100%</div>
                <div className="text-lg">Carbon Neutral Operations</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <div className="text-3xl font-bold text-amber-400 mb-2">1000+</div>
                <div className="text-lg">Farmers Empowered</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <div className="text-3xl font-bold text-amber-400 mb-2">Zero</div>
                <div className="text-lg">Child Labour Incidents</div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/about" 
                className="bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn About Us
              </Link>
              <Link 
                to="/services" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-green-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
